import { useState, useRef, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import { cn } from "@/lib/utils"
import { Settings, LogOut, ChevronDown } from "lucide-react"
import { Breadcrumb, getBreadcrumbsForPath } from "@/components/app/breadcrumb"

interface TopNavProps {
  onLogoutClick: () => void
}

export function TopNav({ onLogoutClick }: TopNavProps) {
  const { user, isAuthenticated } = useAuth0()
  const navigate = useNavigate()
  const location = useLocation()
  const breadcrumbItems = getBreadcrumbsForPath(location.pathname, location.search)

  const [isAvatarOpen, setIsAvatarOpen] = useState(false)
  const avatarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (avatarRef.current && !avatarRef.current.contains(event.target as Node)) {
        setIsAvatarOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const userInitials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || "U"

  return (
    <div className="bg-card">
      {/* Border lives on the h-14 header itself (not this wrapper) so it sits at
          the exact same y as the sidebar header's divider — one continuous line. */}
      <header className="flex h-14 items-center justify-between border-b border-border/40 px-6">
        {/* Brand + Breadcrumb */}
        <div className="flex items-center gap-3">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        {/* Right side: Avatar */}
        <div className="flex items-center gap-3 justify-end">
          <div ref={avatarRef} className="relative">
            <button
              onClick={() => setIsAvatarOpen(!isAvatarOpen)}
              className={cn(
                "flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent transition-colors",
                isAvatarOpen && "bg-accent"
              )}
            >
              {user?.picture ? (
                <img src={user.picture} alt={user.name || "User"} className="h-8 w-8 rounded-full" />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
                  {userInitials}
                </div>
              )}
              <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", isAvatarOpen && "rotate-180")} />
            </button>

            {isAvatarOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 rounded-md border bg-popover shadow-lg z-50">
                {isAuthenticated && user && (
                  <div className="border-b border-border/40 px-3 py-2">
                    <div className="text-sm font-medium truncate">{user.name || user.email}</div>
                    {user.email && user.name && (
                      <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                    )}
                  </div>
                )}
                <div className="p-1">
                  <button
                    onClick={() => { setIsAvatarOpen(false); navigate("/settings") }}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    <span>Settings</span>
                  </button>
                  <button
                    onClick={() => { setIsAvatarOpen(false); onLogoutClick() }}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
    </div>
  )
}
